import { createFileRoute, useNavigate, useParams } from '@tanstack/react-router';
import { Alert, Button, Card, Center, Group, Stack, Text, TextInput, Title } from '@mantine/core';
import { motion } from 'framer-motion';
import { useState } from 'react';

function InviteAccept() {
  const { token } = useParams({ from: '/auth/invite/$token' });
  const navigate = useNavigate();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const accept = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch('/api/auth/invite/accept', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ token, firstName, lastName, password }),
      });
      const json = (await res.json()) as { ok: boolean; error?: string };
      if (!json.ok) {
        setError(json.error ?? 'Invite acceptance failed');
        return;
      }
      navigate({ to: '/vendor' });
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
      <Center mih={500}>
        <Card maw={460} w="100%">
          <Stack>
            <Title order={3}>You've been invited to LeadSync</Title>
            <Text c="dimmed" size="sm">
              Set up your account to join the team. If you already have a LeadSync account with this
              email, leave the password blank — we'll just attach you to the workspace.
            </Text>

            {error && <Alert color="red">{error}</Alert>}

            <Group grow>
              <TextInput
                label="First name"
                value={firstName}
                onChange={(e) => setFirstName(e.currentTarget.value)}
              />
              <TextInput
                label="Last name"
                value={lastName}
                onChange={(e) => setLastName(e.currentTarget.value)}
              />
            </Group>
            <TextInput
              label="Password (only required if you don't have an account yet)"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.currentTarget.value)}
              minLength={8}
            />
            <Button onClick={accept} loading={submitting}>
              Accept invite
            </Button>
          </Stack>
        </Card>
      </Center>
    </motion.div>
  );
}

export const Route = createFileRoute('/auth/invite/$token')({ component: InviteAccept });
